import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, ChevronLeft, AlertCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { resolveImage } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Cart() {
  const { items, setQty, remove, totals } = useCart();
  const [stockMap, setStockMap] = useState({}); // { product_id: { out_of_stock, in_db } }

  useEffect(() => {
    if (items.length === 0) { setStockMap({}); return; }
    const ids = items.map((i) => i.product_id).join(",");
    api.get("/stock-check", { params: { ids } })
      .then(({ data }) => setStockMap(data || {}))
      .catch(() => setStockMap({}));
  }, [items]);

  if (items.length === 0) {
    return (
      <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-24 text-center" data-testid="cart-empty">
        <SEO title="Your Cart" description="Review the pieces in your Star Jewels cart and proceed to secure checkout." path="/cart" noindex />
        <ShoppingBag size={40} className="mx-auto text-sj-muted" />
        <h1 className="font-display text-3xl mt-6 font-light">Your bag is empty</h1>
        <p className="text-sj-muted mt-3">Add a little starlight to your day.</p>
        <Link to="/shop" className="btn-primary mt-10" data-testid="cart-empty-shop-btn">
          Shop the collection <ArrowRight size={16} />
        </Link>
      </main>
    );
  }

  const shipping = totals.subtotal >= 1499 ? 0 : 79;
  const total = totals.subtotal + shipping;
  const hasBlocked = items.some((it) => {
    const s = stockMap[it.product_id];
    return s && (s.out_of_stock || s.in_db === false);
  });

  return (
    <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-14 sm:py-20" data-testid="cart-page">
      <SEO title="Your Cart" description="Review the pieces in your Star Jewels cart and proceed to secure checkout." path="/cart" noindex />
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-sj-muted hover:text-sj-goldDark mb-6">
        <ChevronLeft size={16} /> Continue shopping
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl font-light">Your bag</h1>
      <div className="mt-2 text-sj-muted text-sm" data-testid="cart-count">{totals.count} {totals.count === 1 ? "item" : "items"}</div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Items */}
        <ul className="lg:col-span-8 divide-y divide-sj-ink/10">
          {items.map((it) => {
            const s = stockMap[it.product_id];
            const oos = s && (s.out_of_stock || s.in_db === false);
            return (
              <li key={it.product_id} className={`py-6 flex items-center gap-4 sm:gap-6 ${oos ? "opacity-80" : ""}`} data-testid={`cart-item-${it.product_id}`}>
                <div className="w-24 sm:w-28 aspect-square overflow-hidden bg-sj-cream shrink-0 relative">
                  {it.image && <img src={resolveImage(it.image, { w: 200 })} alt="" className={`w-full h-full object-contain p-2 ${oos ? "opacity-60" : ""}`} loading="lazy" decoding="async" width="200" height="200" />}
                  {oos && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
                      <span className="text-[10px] tracking-widest uppercase font-semibold text-sj-rose bg-white/90 px-2 py-0.5 rounded-full" data-testid={`cart-oos-badge-${it.product_id}`}>
                        Out of stock
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.product_id}`} className="font-display text-base sm:text-lg hover:text-sj-goldDark line-clamp-2">
                    {it.name}
                  </Link>
                  <div className="mt-1 text-sm text-sj-muted">₹{Number(it.price).toLocaleString("en-IN")}</div>
                  {oos && (
                    <div className="mt-2 text-xs text-sj-rose flex items-center gap-1.5" data-testid={`cart-oos-note-${it.product_id}`}>
                      <AlertCircle size={12} /> Currently unavailable — please remove to continue.
                    </div>
                  )}
                  {!oos && (
                    <div className="mt-3 inline-flex items-center rounded-full border border-sj-ink/20">
                      <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="px-3 py-1 text-sm" data-testid={`qty-minus-${it.product_id}`}>−</button>
                      <span className="px-2 text-sm min-w-[24px] text-center" data-testid={`qty-value-${it.product_id}`}>{it.quantity}</span>
                      <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="px-3 py-1 text-sm" data-testid={`qty-plus-${it.product_id}`}>+</button>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-display text-base sm:text-lg" data-testid={`line-total-${it.product_id}`}>
                    ₹{Number(it.price * it.quantity).toLocaleString("en-IN")}
                  </div>
                  <button onClick={() => remove(it.product_id)} className="mt-2 text-xs text-sj-muted hover:text-sj-rose inline-flex items-center gap-1" data-testid={`remove-${it.product_id}`}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Summary */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit bg-sj-cream rounded-md p-6 sm:p-8" data-testid="cart-summary">
          <div className="overline">Order summary</div>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-sj-inkSoft">Subtotal</span>
              <span data-testid="summary-subtotal">₹{totals.subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sj-inkSoft">Shipping</span>
              <span data-testid="summary-shipping">{shipping === 0 ? "Free" : `₹${shipping}`}</span>
            </div>
            {shipping > 0 && (
              <div className="text-xs text-sj-muted">
                Spend ₹{(1499 - totals.subtotal).toLocaleString("en-IN")} more for free shipping.
              </div>
            )}
          </div>
          <div className="my-5 h-px bg-sj-ink/10" />
          <div className="flex justify-between items-baseline">
            <span className="font-display text-lg">Total</span>
            <span className="font-display text-2xl" data-testid="summary-total">₹{total.toLocaleString("en-IN")}</span>
          </div>
          <Link
            to="/checkout"
            onClick={(e) => { if (hasBlocked) { e.preventDefault(); } }}
            className={`btn-primary w-full justify-center mt-6 ${hasBlocked ? "opacity-40 pointer-events-none cursor-not-allowed" : ""}`}
            aria-disabled={hasBlocked}
            data-testid="cart-checkout-btn"
          >
            {hasBlocked ? "Remove out-of-stock item to continue" : (<>Checkout <ArrowRight size={16} /></>)}
          </Link>
          {hasBlocked && (
            <div className="mt-3 text-xs text-sj-rose flex items-center justify-center gap-1.5" data-testid="cart-blocked-note">
              <AlertCircle size={12} /> One or more items are unavailable — please remove them to proceed.
            </div>
          )}
          <p className="text-xs text-sj-muted mt-4 text-center">Secure payment via Razorpay. UPI, cards & netbanking.</p>
        </aside>
      </div>
    </main>
  );
}
